import { useShallowStableValue } from '@/hooks/use-shallow-stable-value'
import { useStableCallbacks } from '@/hooks/use-stable-callbacks'
import { PRINT_BLEED_MARGIN_PT } from '@/lib/document/print-bleed'
import type { DocumentLayout } from '@/contexts/document-layout-context'
import type { EditorCommandState } from '@/contexts/editor-command-state-context'
import type { EditorCommands } from '@/contexts/editor-commands-context'
import { isUngroupUnavailable } from '@/lib/groups/ungroup-selection'
import type { EditorSettings } from '@/contexts/editor-settings-context'
import type { EditorTools } from '@/contexts/editor-tools-context'
import type { TextDefaults } from '@/contexts/text-defaults-context'
import type {
  HomeChainEditing,
  HomeDocumentLayout,
  HomeEditorSettings,
  HomeFoundation,
  HomeHistory,
  HomeMenuCommands,
  HomePersistence,
  HomeShapeEditing,
  HomeToolsAndSelection,
  HomeZoomAndSpread,
  HomeZoomCommands,
} from '../types/home-groups'

type EditorContextValuesDeps = Pick<
  HomeFoundation,
  | 'shapeManagerRef'
  | 'centerCanvasRef'
  | 'leftPanelRef'
  | 'activeNewTextBoxStyle'
  | 'setShapeTextEditRevision'
  | 'shapeTextEditRevision'
  | 'setIsCornerResizeActive'
  | 'handleTextStyleUpdate'
  | 'handleKerningAtCaret'
  | 'handleTextRunStyleAppliedForPanel'
> &
  Pick<HomeZoomAndSpread, 'spreadId' | 'spreadIndex'> &
  Pick<
    HomeDocumentLayout,
    | 'gridMode'
    | 'snapToGrid'
    | 'viewSettings'
    | 'showGuides'
    | 'showSmartGuides'
    | 'snapToGuides'
    | 'snapGuideDistancePt'
    | 'nudgeDistancePt'
    | 'rulerUnit'
    | 'unitOfMeasure'
    | 'columns'
    | 'rows'
    | 'colGutter'
    | 'rowGutter'
    | 'defaultGuideGuttersLinked'
    | 'isGuidesDialogOpen'
    | 'preferencesValues'
    | 'bookSettingsPayload'
    | 'totalPages'
    | 'pageViewMode'
    | 'resolvedLayout'
    | 'documentLayout'
    | 'rightPageOrigin'
    | 'mirrorSpineAxisX'
    | 'isMirrorMoveHovering'
    | 'setIsMirrorMoveHovering'
  > &
  Pick<
    HomeEditorSettings,
    | 'handleGuidesDialogOpenChange'
    | 'persistShowGuides'
    | 'persistShowSmartGuides'
    | 'handleSnapToGuidesToggle'
    | 'handlePreferencesChange'
    | 'handleSnapToGridToggle'
    | 'handleToggleFullPica'
    | 'handleToggleHalfPica'
    | 'handleShowBleedBarToggle'
    | 'handleShowTextLinkingToggle'
    | 'handleShowStickyNotesToggle'
  > &
  Pick<
    HomeToolsAndSelection,
    | 'activeItem'
    | 'setActiveItem'
    | 'handleActivateTool'
    | 'selectedShape'
    | 'handleSetSelectedShape'
    | 'selectedPenTool'
    | 'setSelectedPenTool'
    | 'selectedPointType'
    | 'setSelectedPointType'
    | 'isPanModeActive'
    | 'togglePanMode'
    | 'deactivatePanMode'
    | 'isNodeEditMode'
    | 'activeObjectId'
    | 'hasSelection'
    | 'hasTextSelection'
    | 'hasObjectSelectionForMenus'
    | 'hasLockedSelectionForMenus'
    | 'hasSelfFrozenSelectionForMenus'
    | 'showUnlockSelectedInObjectMenus'
    | 'showUnfreezeSelectedInObjectMenus'
    | 'hasLockedSelectedTextForPanels'
    | 'hasAnyLockedObjects'
    | 'hasAnyFrozenObjects'
    | 'handleCheckSpelling'
    | 'isCaptionEnabled'
  > &
  Pick<
    HomePersistence,
    | 'canUndo'
    | 'canRedo'
    | 'getUndoStackSize'
    | 'handleTransparencyCommitApplied'
    | 'syncActiveFromShapeSelection'
    | 'checkpointDefaultFontName'
    | 'checkpointDefaultFontSize'
    | 'handleSpreadIndexChange'
  > &
  Pick<HomeHistory, 'handleUndo' | 'handleRedo'> &
  Pick<
    HomeZoomCommands,
    | 'handleZoomIn'
    | 'handleZoomOut'
    | 'handleZoomFit'
    | 'handleActualSize'
    | 'handleZoomToSelection'
    | 'handleZoomPercentCommit'
  > &
  Pick<
    HomeShapeEditing,
    | 'photoEffectsOpen'
    | 'setPhotoEffectsOpen'
    | 'removingBackgroundShapeIds'
    | 'handleShapeSelectionHistory'
    | 'handleShapesNudge'
    | 'handleEditPath'
    | 'handleCropPhoto'
    | 'handlePhotoEffects'
    | 'convertTextToShapeDisabledForMenus'
    | 'handleConvertTextToShape'
    | 'textOnPathDisabledForMenus'
    | 'editPathDisabledForMenus'
    | 'runTextOnPathCommand'
    | 'handleRemovePhotoFromShape'
    | 'handleRemovePhotoFromPhotosPanel'
    | 'handleRemoveBackground'
    | 'handleRemoveBackgroundFromPhotosPanel'
    | 'isRemovingBackgroundForSelection'
  > &
  Pick<HomeChainEditing, 'handleAddCaption'> &
  Pick<
    HomeMenuCommands,
    | 'handleLogout'
    | 'handleMenuToggle'
    | 'handleClose'
    | 'handleNavigate'
    | 'clearActiveObject'
    | 'handleSelectAll'
    | 'handleSelectNone'
    | 'handleGroupObjects'
    | 'handleUngroupObjects'
    | 'handleLockSelectedObjects'
    | 'handleUnlockSelectedObjects'
    | 'handleUnlockAllObjects'
    | 'handleFreezeSelectedObjects'
    | 'handleUnfreezeSelectedObjects'
    | 'handleUnfreezeAllObjects'
    | 'handleCutSelection'
    | 'handleCopySelection'
    | 'handleDuplicateSelection'
    | 'copyDisabledForMenus'
    | 'cutDisabledForMenus'
    | 'handlePasteSelection'
    | 'handlePasteSelectionInPlace'
    | 'handleDeleteSelection'
    | 'handleSetColumnsRows'
    | 'handleNodeEditPointTypeChange'
    | 'handleExitPenTool'
    | 'handleToggleNodeEditPenMode'
    | 'handleNodeEditModeChange'
    | 'handleCanvasBackgroundClick'
    | 'handleCanvasSelectionChange'
    | 'handleSelectAllApplied'
    | 'handleCanvasShapeClick'
    | 'handleActivateTextTool'
    | 'handleExpandTypePanel'
    | 'handleActivateSelectTool'
  >

export interface EditorContextValues {
  settings: EditorSettings
  layout: DocumentLayout
  textDefaults: TextDefaults
  tools: EditorTools
  commands: EditorCommands
  commandState: EditorCommandState
}

/**
 * Builds the six editor context values from home's group results. Each value keeps its identity
 * until its contents change (`useShallowStableValue`), and every function in them is stable
 * (`useStableCallbacks`), so a home re-render only re-renders the consumers of what changed.
 *
 * Values home reads from refs at render time (paste candidate, group isolation, can-group) are
 * read here on every render, exactly as the props they replace were.
 */
export function useEditorContextValues(deps: EditorContextValuesDeps): EditorContextValues {
  const {
    shapeManagerRef,
    centerCanvasRef,
    leftPanelRef,
    activeNewTextBoxStyle,
    resolvedLayout,
    documentLayout,
    bookSettingsPayload,
  } = deps

  const settingsActions = useStableCallbacks({
    setGuidesDialogOpen: deps.handleGuidesDialogOpenChange,
    setColumnsRows: deps.handleSetColumnsRows,
    setPreferences: deps.handlePreferencesChange,
    toggleFullPica: deps.handleToggleFullPica,
    toggleHalfPica: deps.handleToggleHalfPica,
    toggleSnapToGrid: deps.handleSnapToGridToggle,
    toggleShowGuides: deps.persistShowGuides,
    toggleShowSmartGuides: deps.persistShowSmartGuides,
    toggleSnapToGuides: deps.handleSnapToGuidesToggle,
    toggleShowBleedBar: deps.handleShowBleedBarToggle,
    toggleShowTextLinking: deps.handleShowTextLinkingToggle,
    toggleShowStickyNotes: deps.handleShowStickyNotesToggle,
  })

  const settings = useShallowStableValue<EditorSettings>({
    gridMode: deps.gridMode,
    snapToGrid: deps.snapToGrid,
    showGuides: deps.showGuides,
    showSmartGuides: deps.showSmartGuides,
    snapToGuides: deps.snapToGuides,
    snapGuideDistance: deps.snapGuideDistancePt,
    nudgeDistancePt: deps.nudgeDistancePt,
    showBleed: deps.viewSettings.showBleed,
    showTextLinking: deps.viewSettings.showLink,
    showStickyNotes: deps.viewSettings.showSticky,
    rulerUnit: deps.rulerUnit,
    unitOfMeasure: deps.unitOfMeasure,
    columns: deps.columns,
    rows: deps.rows,
    colGutter: deps.colGutter,
    rowGutter: deps.rowGutter,
    defaultGuttersLinked: deps.defaultGuideGuttersLinked,
    isGuidesDialogOpen: deps.isGuidesDialogOpen,
    preferencesValues: deps.preferencesValues,
    ...settingsActions,
  })

  const layout = useShallowStableValue<DocumentLayout>({
    spreadId: deps.spreadId,
    spreadIndex: deps.spreadIndex,
    totalPages: deps.totalPages,
    pageViewMode: deps.pageViewMode,
    bleedWidth: resolvedLayout.bleedWidth,
    bleedHeight: resolvedLayout.bleedHeight,
    trimWidth: resolvedLayout.trimWidth,
    trimHeight: resolvedLayout.trimHeight,
    bleedMargin: PRINT_BLEED_MARGIN_PT,
    liveInset: documentLayout.liveInset,
    centerGap: documentLayout.centerGap,
    marginInside: documentLayout.marginInside,
    marginOutside: documentLayout.marginOutside,
    marginTop: documentLayout.marginTop,
    marginBottom: documentLayout.marginBottom,
    pageInnerWidth: resolvedLayout.pageInnerWidth,
    pageInnerHeight: resolvedLayout.pageInnerHeight,
    rightPageOriginX: deps.rightPageOrigin.x,
    rightPageOriginY: deps.rightPageOrigin.y,
    mirrorSpineAxisX: deps.mirrorSpineAxisX,
  })

  const textDefaults = useShallowStableValue<TextDefaults>({
    newTextBoxKerning: activeNewTextBoxStyle.kerning,
    newTextBoxTracking: activeNewTextBoxStyle.tracking,
    newTextBoxUnderline: activeNewTextBoxStyle.underline,
    newTextBoxAlignment: activeNewTextBoxStyle.alignment,
    defaultFontSize:
      bookSettingsPayload?.job?.defaultFontSize ?? deps.checkpointDefaultFontSize ?? undefined,
    defaultFontFamilyId: bookSettingsPayload?.job?.defaultFontFamilyId,
    defaultFontName: deps.checkpointDefaultFontName ?? undefined,
  })

  const toolActions = useStableCallbacks({
    setActiveItem: deps.setActiveItem,
    activateTool: deps.handleActivateTool,
    setSelectedShape: deps.handleSetSelectedShape,
    setSelectedPenTool: deps.setSelectedPenTool,
    setSelectedPointType: deps.setSelectedPointType,
    togglePanMode: deps.togglePanMode,
    deactivatePanMode: deps.deactivatePanMode,
    setMirrorMoveHovering: deps.setIsMirrorMoveHovering,
  })

  const tools = useShallowStableValue<EditorTools>({
    activeItem: deps.activeItem,
    selectedShape: deps.selectedShape,
    selectedPenTool: deps.selectedPenTool,
    selectedPointType: deps.selectedPointType,
    isPanModeActive: deps.isPanModeActive,
    isNodeEditMode: deps.isNodeEditMode,
    isMirrorMoveHovering: deps.isMirrorMoveHovering,
    ...toolActions,
  })

  const commands = useStableCallbacks<EditorCommands>({
    closeEditor: deps.handleClose,
    logout: deps.handleLogout,
    menuToggle: deps.handleMenuToggle,
    navigateSpread: deps.handleNavigate,
    changeSpreadIndex: deps.handleSpreadIndexChange,
    undo: deps.handleUndo,
    redo: deps.handleRedo,
    cut: deps.handleCutSelection,
    copy: deps.handleCopySelection,
    duplicate: deps.handleDuplicateSelection,
    paste: deps.handlePasteSelection,
    pasteInPlace: deps.handlePasteSelectionInPlace,
    deleteSelection: deps.handleDeleteSelection,
    selectAll: deps.handleSelectAll,
    selectNone: deps.handleSelectNone,
    checkSpelling: deps.handleCheckSpelling,
    group: deps.handleGroupObjects,
    ungroup: deps.handleUngroupObjects,
    lockSelected: deps.handleLockSelectedObjects,
    unlockSelected: deps.handleUnlockSelectedObjects,
    freezeSelected: deps.handleFreezeSelectedObjects,
    unfreezeSelected: deps.handleUnfreezeSelectedObjects,
    unlockAll: deps.handleUnlockAllObjects,
    unfreezeAll: deps.handleUnfreezeAllObjects,
    textOnPath: deps.runTextOnPathCommand,
    convertTextToShape: deps.handleConvertTextToShape,
    editPath: deps.handleEditPath,
    editSelectedPath: () => {
      if (deps.activeObjectId) deps.handleEditPath(deps.activeObjectId)
    },
    zoomIn: deps.handleZoomIn,
    zoomOut: deps.handleZoomOut,
    zoomFit: deps.handleZoomFit,
    actualSize: deps.handleActualSize,
    zoomToSelection: deps.handleZoomToSelection,
    commitZoomPercent: deps.handleZoomPercentCommit,
    addCaption: deps.handleAddCaption,
    cropPhoto: deps.handleCropPhoto,
    togglePhotoEffects: deps.handlePhotoEffects,
    setPhotoEffectsOpen: deps.setPhotoEffectsOpen,
    removePhotoFromPanel: deps.handleRemovePhotoFromPhotosPanel,
    removeBackgroundFromPanel: deps.handleRemoveBackgroundFromPhotosPanel,
    removePhotoFromShape: deps.handleRemovePhotoFromShape,
    removeBackgroundFromShape: (shapeId: string) => {
      void deps.handleRemoveBackground(shapeId)
    },
    transparencyCommitApplied: deps.handleTransparencyCommitApplied,
    applyTextStyle: deps.handleTextStyleUpdate,
    applyKerningAtCaret: deps.handleKerningAtCaret,
    restoreShapeTextFocus: () => shapeManagerRef.current?.restoreShapeTextEditorFocus(),
    getEditingShapeTextSelectionStyle: () =>
      shapeManagerRef.current?.getEditingShapeTextSelectionStyle() ?? null,
    getEditingShapeTextId: () => shapeManagerRef.current?.getEditingShapeTextId() ?? null,
    getEditingShapeTextKerningState: () =>
      shapeManagerRef.current?.getEditingShapeTextKerningState() ?? null,
    canvasShapeClick: deps.handleCanvasShapeClick,
    canvasBackgroundClick: deps.handleCanvasBackgroundClick,
    canvasSelectionChange: deps.handleCanvasSelectionChange,
    shapeSelectionHistory: deps.handleShapeSelectionHistory,
    selectAllApplied: deps.handleSelectAllApplied,
    selectNoneApplied: deps.clearActiveObject,
    activateTextTool: deps.handleActivateTextTool,
    activateSelectTool: deps.handleActivateSelectTool,
    expandTypePanel: deps.handleExpandTypePanel,
    shapeTextEditStateChange: () => {
      deps.setShapeTextEditRevision((r) => r + 1)
    },
    deleteShapesHandled: () => {
      leftPanelRef.current?.focusSelectTool?.()
    },
    shapesNudge: deps.handleShapesNudge,
    liveSelectionGeometrySync: deps.syncActiveFromShapeSelection,
    cornerResizeGestureActiveChange: deps.setIsCornerResizeActive,
    checkpointTextBoxesApplied: deps.handleTextRunStyleAppliedForPanel,
    getUndoStackSize: deps.getUndoStackSize,
    exitNodeEditMode: () => centerCanvasRef.current?.exitNodeEditMode(),
    nodeEditModeChange: deps.handleNodeEditModeChange,
    nodeEditPointTypeChange: deps.handleNodeEditPointTypeChange,
    toggleNodeEditPenMode: deps.handleToggleNodeEditPenMode,
    exitPenTool: deps.handleExitPenTool,
  })

  const isInGroupIsolationMode = Boolean(centerCanvasRef.current?.isInGroupIsolationMode())
  const hasPasteCandidate = Boolean(centerCanvasRef.current?.hasPasteCandidate())
  // Same test as the canvas's `isTextEditSessionActive`; `shapeTextEditRevision` re-renders home
  // whenever an edit session starts or ends, so this read stays current.
  const isShapeTextEditing = Boolean(shapeManagerRef.current?.getEditingShapeTextId())

  const commandState = useShallowStableValue<EditorCommandState>({
    canUndo: deps.canUndo,
    canRedo: deps.canRedo,
    selectNoneDisabled: !deps.hasSelection && !deps.hasTextSelection,
    cutDisabled: deps.cutDisabledForMenus,
    copyDisabled: deps.copyDisabledForMenus,
    // Duplicate copies first, so it shares Copy's gate (as the context-menu row does). Unlike Copy
    // it is objects-only: mid text edit `duplicateSelection` no-ops, so the row must not look live.
    duplicateDisabled: deps.copyDisabledForMenus || isShapeTextEditing,
    pasteDisabled: !hasPasteCandidate,
    pasteInPlaceDisabled: !hasPasteCandidate,
    deleteDisabled:
      !deps.hasObjectSelectionForMenus ||
      deps.hasLockedSelectionForMenus ||
      deps.hasSelfFrozenSelectionForMenus,
    groupDisabled: !shapeManagerRef.current?.canGroupSelectedObjects(),
    ungroupDisabled: isUngroupUnavailable(shapeManagerRef.current),
    lockSelectedDisabled: !deps.hasObjectSelectionForMenus || isInGroupIsolationMode,
    unlockSelectedDisabled: !deps.hasObjectSelectionForMenus,
    freezeSelectedDisabled: !deps.hasObjectSelectionForMenus || isInGroupIsolationMode,
    unfreezeSelectedDisabled: !deps.showUnfreezeSelectedInObjectMenus || isInGroupIsolationMode,
    unlockAllDisabled: !deps.hasAnyLockedObjects,
    unfreezeAllDisabled: !deps.hasAnyFrozenObjects,
    showUnlockSelected: deps.showUnlockSelectedInObjectMenus,
    showUnfreezeSelected: deps.showUnfreezeSelectedInObjectMenus,
    convertTextToShapeDisabled: deps.convertTextToShapeDisabledForMenus,
    textOnPathDisabled: deps.textOnPathDisabledForMenus,
    canEditSelectedPath: Boolean(deps.activeObjectId && !deps.editPathDisabledForMenus),
    captionEnabled: deps.isCaptionEnabled,
    isInGroupIsolationMode,
    textStyleDisabled: deps.hasLockedSelectedTextForPanels,
    shapeTextEditRevision: deps.shapeTextEditRevision,
    lockedSelectionDisablesPropertyPanels: deps.hasLockedSelectionForMenus,
    photoEffectsOpen: deps.photoEffectsOpen,
    isRemovingBackgroundForSelection: deps.isRemovingBackgroundForSelection,
    removingBackgroundShapeIds: deps.removingBackgroundShapeIds,
  })

  return { settings, layout, textDefaults, tools, commands, commandState }
}
